import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { from, map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class KeycloakService {
  private keycloakAuth: any;

  constructor(private router: Router) {}

  initKeycloak(): Promise<boolean> {
    if (!environment.login) {
      console.log('Login disabled — skipping Keycloak init');
      return Promise.resolve(true);
    }

    this.keycloakAuth = new Keycloak({
      url: environment.keycloakUrl,
      realm: environment.keycloakRealm,
      clientId: environment.keycloakClientId,
    });

    return this.keycloakAuth
      .init({ onLoad: 'login-required', checkLoginIframe: false,flow: 'standard', pkceMethod: 'S256' })
      .then((authenticated: boolean) => {
        console.log(`Initialized Keycloak, authenticated: ${authenticated}`);
        return authenticated;
      })
      .catch((error: any) => {
        console.error('Error during Keycloak initialization', error);
        throw error;
      });
  }

  isLoggedIn(): Observable<boolean> {
    if (!environment.login) {
      return from(Promise.resolve(true));
    }
    return from(
      Promise.resolve(
        !!this.keycloakAuth.token && !this.keycloakAuth.isTokenExpired()
      )
    );
  }

  login() {
    if (!environment.login) {
      console.log('Login disabled — skipping login');
      return;
    }
    return this.keycloakAuth.login();
  }

  logout() {
    if (!environment.login) {
      console.log('Login disabled — skipping logout');
      return;
    }
    return this.keycloakAuth.logout();
  }

  async getProfile() {
    if (!environment.login) {
      return {
        username: 'guest',
        firstName: 'Guest',
        lastName: '',
        email: 'guest@example.com',
      };
    }
    return this.keycloakAuth.loadUserProfile();
  }

  getToken(): Promise<string> {
    if (!environment.login) {
      return Promise.resolve('');
    }
    return Promise.resolve(this.keycloakAuth.token);
  }

  renewToken(): Promise<string> {
    if (!environment.login) {
      console.log('🔁 Token renew skipped — login disabled');
      return Promise.resolve('');
    }

    return this.keycloakAuth.updateToken(60)
      .then((refreshed: boolean) => {
        if (refreshed) {
          console.log('🔄 Access token successfully refreshed');
        } else {
          console.log('⏳ Access token still valid, no need to refresh');
        }
        return this.keycloakAuth.token;
      })
      .catch((error: any) => {
        console.error('❌ Failed to refresh token', error);
        return '';
      });
  }


  updateUser() {
    if (!environment.login) {
      console.log('Login disabled — updateUser skipped');
      return;
    }
    return this.keycloakAuth.accountManagement();
  }

  getUserRoles(): string[] {
    if (!environment.login || !this.keycloakAuth?.authenticated) {
      return ['guest'];
    }

    const tokenParsed = this.keycloakAuth.tokenParsed;
    let roles: string[] = [];

    if (tokenParsed?.realm_access?.roles) {
      roles = roles.concat(tokenParsed.realm_access.roles);
    }

    const clientId = environment.keycloakClientId;
    if (
      tokenParsed?.resource_access?.[clientId]?.roles &&
      Array.isArray(tokenParsed.resource_access[clientId].roles)
    ) {
      roles = roles.concat(tokenParsed.resource_access[clientId].roles);
    }

    return roles;
  }

  hasRole(role: string): boolean {
    const roles = this.getUserRoles();
    return roles.includes(role);
  }

  checkKeycloakLoginValidity() {
    if (!environment.login) {
      console.log('Login disabled — skipping validity check');
      return;
    }

    if (this.keycloakAuth.authenticated) {
      const tokenParsed = this.keycloakAuth.tokenParsed;
      if (tokenParsed) {
        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        const expiresIn = tokenParsed.exp - currentTime; // Remaining validity in seconds
        console.log(`Token expires in ${expiresIn} seconds`);
      } else {
        console.warn('Token information not available');
      }
    } else {
      console.warn('User not authenticated');
    }
  }
}
